import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import api, { API_BASE_URL } from '../helpers/api';
import { getSingleEstablishment } from '../helpers/establishments';
import { getCategories } from '../helpers/categories';
import { deleteMedia, uploadMultipleMedia } from '../helpers/upload';
import ReorderableGallery from '../components/ReorderableGallery';
import TagSelector from '../components/TagSelector';
import { getTags } from '../helpers/tags';
import { GUADELOUPE_CITIES } from '../helpers/cities';
import Select from 'react-select';
import { toast } from 'react-toastify';

const EstablishmentEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const editorRef = useRef(null);

  const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  const getDefaultOpeningHours = () => {
    return JOURS.map(day => ({
      day,
      hours: ''
    }));
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    openingHours: getDefaultOpeningHours(),
    category: '',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
    email: null,
    website: '',
    price: '€',
    is_verified: false,
    location: {
      lat: '',
      long: ''
    },
    images: [],
    tags: []
  });

  const handleImageReorder = (newImages) => {
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  useEffect(() => {
    fetchCategories();
    if (id !== 'new') {
      fetchEstablishment();
    } else {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await getTags();
        setAvailableTags(response);
      } catch (err) {
        console.error('Erreur lors du chargement des tags:', err);
        setError('Erreur lors du chargement des tags');
      }
    };

    fetchTags();
  }, []);

  const handleTagsChange = (newTags) => {
    setFormData(prev => ({
      ...prev,
      tags: newTags
    }));
  };

  const fetchCategories = async () => {
    try {
      const response = await getCategories();
      setCategories(response);
    } catch (err) {
      console.error('Erreur lors du chargement des catégories:', err);
      setError('Erreur lors du chargement des catégories');
    }
  };

  const fetchEstablishment = async () => {
    try {
      const response = await api.get(`/establishments/${id}?populate=*`);
      const data = response.data.data;

      setFormData({
        name: data.name || '',
        description: data.description || '',
        openingHours: data.openingHours || getDefaultOpeningHours(),
        category: data.category?.id || '',
        address: data.address || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        phone: data.phone || '',
        email: data.email || null,
        website: data.website || '',
        price: data.price || '€',
        is_verified: data.is_verified || false,
        location: data.location || { lat: '', long: '' },
        images: data.images || [],
        tags: data.tags || []
      });
    } catch (err) {
      console.error('Erreur lors du chargement du spot:', err);
      setError('Erreur lors du chargement du spot');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        [name]: value
      }
    }));
  };

  const handleHoursChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      openingHours: prev.openingHours.map((hour, i) =>
        i === index ? { ...hour, hours: value } : hour
      )
    }));
  };

  const stripSystemFields = (data) => {
    if (!data) return data;

    // Fields to strip completely
    const fieldsToStrip = ['id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt'];

    // Fields where we need to keep the id (relations)
    const relationFields = ['category', 'tags', 'images'];

    if (Array.isArray(data)) {
      return data.map(item => stripSystemFields(item));
    }

    if (typeof data === 'object') {
      const cleaned = {};

      for (const [key, value] of Object.entries(data)) {
        // Skip system fields
        if (fieldsToStrip.includes(key)) continue;

        // Special handling for location
        if (key === 'location') {
          if (value && typeof value === 'object') {
            if ('lat' in value || 'long' in value) {
              // If it has lat/long, keep those
              cleaned[key] = {
                lat: value.lat,
                long: value.long
              };
            } else {
              // Otherwise treat as relation
              cleaned[key] = { id: value.id };
            }
          } else if (typeof value === 'number') {
            // If location is just a number, convert to relation object
            cleaned[key] = { id: value };
          }
          continue;
        }

        // Handle relation fields
        if (relationFields.includes(key)) {
          if (Array.isArray(value)) {
            // Handle array relations (many-to-many)
            cleaned[key] = value.map(item => ({ id: item.id }));
          } else if (value && typeof value === 'object') {
            // Handle single relations (one-to-one, many-to-one)
            cleaned[key] = { id: value.id };
          } else if (typeof value === 'number') {
            // If value is just a number, convert to relation object
            cleaned[key] = { id: value };
          } else {
            cleaned[key] = value;
          }
          continue;
        }

        // Special handling for openingHours - keep all fields except system fields
        if (key === 'openingHours' && Array.isArray(value)) {
          cleaned[key] = value.map(({ hours, day }) => ({ hours, day }));
          continue;
        }

        // Handle regular fields recursively
        if (Array.isArray(value)) {
          cleaned[key] = value.map(item =>
            typeof item === 'object' && item !== null ? stripSystemFields(item) : item
          );
        } else if (typeof value === 'object' && value !== null) {
          cleaned[key] = stripSystemFields(value);
        } else {
          cleaned[key] = value;
        }
      }

      return cleaned;
    }

    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitDisabled(true);
    let newId;

    try {
      // Create a copy of the form data to update
      let updatedFormData = { ...formData };

      // Get the current editor content
      if (editorRef.current) {
        updatedFormData.description = editorRef.current.getContent();
      }

      // Get non-localized fields
      const cleanedData = stripSystemFields(updatedFormData);

      if (id === 'new') {
        // Create new establishment
        const response = await api.post('/establishments', {
          data: cleanedData
        });

        newId = response.data.data.documentId;
      } else {
        // Update existing establishment
        await api.put(`/establishments/${id}`, {
          data: cleanedData
        });
      }
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Erreur lors de la sauvegarde');
      setSubmitDisabled(false);
      toast.error('Erreur lors de la sauvegarde. Merci de réessayer.');
    } finally {
      toast.success(id === 'new' ? 'Ajouté avec succès !' : 'Modifié avec succès !');
      setSubmitDisabled(false);
      if (id === 'new') {
        navigate(`/establishments/${newId}`);
      }
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);

    try {
      const uploadedImages = await uploadMultipleMedia(files);

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages]
      }));
    } catch (err) {
      console.error('Erreur lors du téléchargement des images:', err);
      setError('Erreur lors du téléchargement des images');
    }
  };

  const handleImageDelete = async (imageId) => {
    try {
      await deleteMedia(imageId);
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter(img => img.id !== imageId)
      }));
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'image:', err);
      setError('Erreur lors de la suppression de l\'image');
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {id === 'new' ? 'Nouveau spot' : 'Modifier le spot'}
        </h2>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate('/establishments')}
        >
          Retour à la liste
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="mb-3">
                  <label className="form-label">Nom</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <Editor
                    apiKey="votre-clé-api-tinymce"
                    onInit={(evt, editor) => editorRef.current = editor}
                    initialValue={formData.description}
                    init={{
                      height: 400,
                      menubar: false,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | blocks | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | help',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Catégorie</label>
                    <Select
                      options={categories.map(category => ({
                        value: category.id,
                        label: category.name
                      }))}
                      value={categories.length > 0 ? categories.find(c => c.id === formData.category) ? {
                        value: formData.category,
                        label: categories.find(c => c.id === formData.category)?.name
                      } : null : null}
                      onChange={(selectedOption) => {
                        setFormData(prev => ({
                          ...prev,
                          category: selectedOption ? selectedOption.value : ''
                        }));
                      }}
                      placeholder="Sélectionner une catégorie..."
                      isClearable
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Gamme de prix</label>
                    <select
                      className="form-select"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                    >
                      <option value="Gratuit">Gratuit</option>
                      <option value="€">€</option>
                      <option value="€€">€€</option>
                      <option value="€€€">€€€</option>
                      <option value="€€€€">€€€€</option>
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Adresse</label>
                  <input
                    type="text"
                    className="form-control mb-2"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Adresse"
                    required
                  />

                  <div className="row">
                    <div className="col-md-8 mb-3">
                      <label className="form-label">Ville</label>
                      <Select
                        options={GUADELOUPE_CITIES.map(city => ({
                          value: city.name,
                          label: `${city.name} (${city.postalCode})`,
                          postalCode: city.postalCode
                        }))}
                        value={formData.city ? {
                          value: formData.city,
                          label: formData.city
                        } : null}
                        onChange={(selectedOption) => {
                          setFormData(prev => ({
                            ...prev,
                            city: selectedOption?.value || '',
                            postalCode: selectedOption?.postalCode || ''
                          }));
                        }}
                        placeholder="Sélectionner une ville..."
                        isClearable
                      />
                    </div>

                    {/* Hidden postal code field */}
                    <div className="col-md-4 mb-3" style={{ display: 'none' }}>
                      <input
                        type="text"
                        className="form-control"
                        name="postalCode"
                        value={formData.postalCode}
                        readOnly
                      />
                    </div>
                  </div>

                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Latitude</label>
                    <input
                      type="text"
                      className="form-control"
                      name="lat"
                      value={formData.location.lat}
                      onChange={handleLocationChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Longitude</label>
                    <input
                      type="text"
                      className="form-control"
                      name="long"
                      value={formData.location.long}
                      onChange={handleLocationChange}
                      required
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Tags</label>
                  <TagSelector
                    selectedTags={formData.tags}
                    availableTags={availableTags}
                    onChange={handleTagsChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-3">
                  <label className="form-label">Images</label>
                  <input
                    type="file"
                    className="form-control mb-2"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                  <ReorderableGallery
                    images={formData.images}
                    baseUrl={API_BASE_URL}
                    onDelete={handleImageDelete}
                    onReorder={handleImageReorder}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Contact</label>
                  <input
                    type="tel"
                    className="form-control mb-2"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Téléphone"
                  />
                  <input
                    type="email"
                    className="form-control mb-2"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                  />
                  <input
                    type="url"
                    className="form-control"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="Site web"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Horaires d'ouverture</label>
                  {formData.openingHours.map((hour, index) => (
                    <div key={hour.day} className="input-group mb-2">
                      <span className="input-group-text" style={{ width: '100px' }}>
                        {hour.day}
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={hour.hours}
                        onChange={(e) => handleHoursChange(index, e.target.value)}
                        placeholder="ex: 9h-18h"
                      />
                    </div>
                  ))}
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="isVerified"
                      name="is_verified"
                      checked={formData.is_verified}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="isVerified">
                      Spot vérifié
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/establishments')}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitDisabled}
              >
                {submitDisabled ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EstablishmentEdit;